import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import dayjs from "dayjs";
import { Pencil, Plus, Trash2 } from "lucide-react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import InputCst from "@components/InputCst/InputCst";
import DataPickerCst from "@components/InputCst/DataPickerCst";
import LOOutcomeModalCst from "@components/LOOutcomeModalCst";
import {
  formatSessionOccupiedError,
  getAllErrorsAsString,
  getEditionCapacityErrorLabel,
  isSessionOverlapError,
} from "@utils/utilsApi";
import { openToast } from "@utils/toastStore";
import { editionPerm, usePermissions } from "@utils/permissions";
import { openSessionWizardModal } from "./SessionWizard/host";
import { SessionDraft } from "./SessionWizard/types";
import { sessionToDraft } from "./SessionWizard/editMapping";
import { useEditionDetail } from "./hooks/useEditionDetail";
import { useEditionSessions } from "./hooks/useEditionSessions";
import { useEditionSubmit } from "./hooks/useEditionSubmit";
import { useActivityDuration } from "./hooks/useActivityDuration";
import WarningDialogCst from "@components/WarningDialogCst";
import { EditionKind } from "./types";
import { ButtonTertiary } from "src/UI/ButtonTertiary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonTertiaryIcon } from "src/UI/ButtonTertiaryIcon";
import PageHeaderV2Cst from "@components/PageHeaderV2Cst";
import { WizardReadOnlyArea } from "@components/WizardCst";

const EDITION_NAME_MAX_LENGTH = 70;
const EDITION_CODE_MAX_LENGTH = 12;
const EDITION_EXTRA_INFO_MAX_LENGTH = 500;

interface EditionFormState {
  edition_name: string;
  edition_code: string;
  publish_date: string | null;
  extra_info: string;
  max_capacity: string;
}

const INITIAL_FORM: EditionFormState = {
  edition_name: "",
  edition_code: "",
  publish_date: null,
  extra_info: "",
  max_capacity: "",
};

const sessionsTotalMinutes = (sessions: SessionDraft[]): number =>
  sessions.reduce((total, s) => {
    if (!s.start_date || !s.end_date) return total;
    return (
      total + Math.abs(dayjs(s.end_date).diff(dayjs(s.start_date), "minute"))
    );
  }, 0);

const AddEditionPage: React.FC = () => {
  const { t } = useTranslation(["editions", "session-wizard"]);
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "", idLO = "", editionId } = useParams();
  const [searchParams] = useSearchParams();

  const kind: EditionKind =
    searchParams.get("kind") === "physical" ? "physical" : "virtual";

  const isEdit = editionId != null && editionId !== "";

  // Creazione: editions.physical.create / editions.virtual.create in base al
  // tipo (come il bottone "Crea edizione" dell'elenco).
  const { can } = usePermissions();
  const canCreateEdition = can(editionPerm(kind, "create"));
  // Modifica (come V1): pagina accessibile con editions.*.retrieve, campi e
  // sessioni modificabili solo con editions.*.update.
  const canViewEdition = can(editionPerm(kind, "retrieve"));
  const canUpdateEdition = can(editionPerm(kind, "update"));
  const readOnly = isEdit && !canUpdateEdition;

  const {
    detail,
    isLoading: detailLoading,
    error: detailError,
  } = useEditionDetail(corporateId, editionId ?? "", kind);
  const { sessions: rawSessions, isLoading: sessionsLoading } =
    useEditionSessions(corporateId, editionId ?? "", kind);

  const loId = isEdit ? detail?.learning_objects?.id : idLO;
  const { activityDuration, activityMaxParticipants } = useActivityDuration(
    corporateId,
    isEdit ? undefined : loId,
  );

  const { submitEdition, isSubmitting } = useEditionSubmit(
    corporateId,
    kind,
    editionId,
  );

  const [form, setForm] = useState<EditionFormState>(INITIAL_FORM);
  const [sessions, setSessions] = useState<SessionDraft[]>([]);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [hydrated, setHydrated] = useState(!isEdit);
  const [durationWarnOpen, setDurationWarnOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);

  const baselineRef = useRef<{
    form: EditionFormState;
    sessions: SessionDraft[];
  }>({ form: INITIAL_FORM, sessions: [] });

  useEffect(() => {
    if (!isEdit || hydrated) return;
    if (detailLoading || sessionsLoading || !detail) return;
    const hydratedForm: EditionFormState = {
      edition_name: detail.edition_name ?? "",
      edition_code: detail.edition_code ?? "",
      publish_date: detail.publish_date
        ? dayjs(detail.publish_date).toISOString()
        : null,
      extra_info: detail.extra_info ?? "",
      max_capacity:
        detail.max_capacity != null ? String(detail.max_capacity) : "",
    };
    const hydratedSessions = rawSessions.map((s) => sessionToDraft(s, kind));
    setForm(hydratedForm);
    setSessions(hydratedSessions);
    baselineRef.current = { form: hydratedForm, sessions: hydratedSessions };
    setHydrated(true);
  }, [
    isEdit,
    hydrated,
    detailLoading,
    sessionsLoading,
    detail,
    rawSessions,
    kind,
  ]);

  useEffect(() => {
    if (isEdit || activityMaxParticipants == null) return;
    setForm((prev) =>
      prev.max_capacity === ""
        ? { ...prev, max_capacity: String(activityMaxParticipants) }
        : prev,
    );
  }, [isEdit, activityMaxParticipants]);

  const update = (patch: Partial<EditionFormState>) => {
    if (readOnly) return;
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const nameTooLong = form.edition_name.length > EDITION_NAME_MAX_LENGTH;
  const codeTooLong = form.edition_code.length > EDITION_CODE_MAX_LENGTH;
  const extraTooLong = form.extra_info.length > EDITION_EXTRA_INFO_MAX_LENGTH;
  const capacityMissing = form.max_capacity === "";
  const capacityInvalid =
    form.max_capacity !== "" && Number(form.max_capacity) < 1;
  const publishDateMissing = !form.publish_date;
  const sessionsBeforePublishDate =
    !publishDateMissing &&
    sessions.some(
      (s) =>
        !!s.start_date &&
        dayjs(s.start_date).isBefore(dayjs(form.publish_date)),
    );
  const publishDateLocked =
    isEdit &&
    !!detail?.publish_date &&
    dayjs().isAfter(dayjs(detail.publish_date));

  const editionContextLabel =
    detail?.learning_objects?.label || detail?.edition_name || undefined;

  const handleAddSession = () => {
    openSessionWizardModal({
      kind,
      corporateId,
      editionId: isEdit ? editionId : undefined,
      contextLabel: editionContextLabel,
      publishDate: form.publish_date,
      otherSessions: sessions,
      onConfirm: (draft) => setSessions((prev) => [...prev, draft]),
    });
  };

  const handleEditSession = (index: number) => {
    const target = sessions[index];
    if (!target) return;
    openSessionWizardModal({
      kind,
      corporateId,
      editionId: isEdit ? editionId : undefined,
      contextLabel: editionContextLabel,
      editSession: target,
      publishDate: form.publish_date,
      otherSessions: sessions.filter((_, i) => i !== index),
      onConfirm: (draft) =>
        setSessions((prev) =>
          prev.map((s, i) => (i === index ? { ...draft, id: s.id } : s)),
        ),
    });
  };

  const handleRemoveSession = (index: number) =>
    setSessions((prev) => prev.filter((_, i) => i !== index));

  const handleReset = () => {
    setForm(isEdit ? baselineRef.current.form : INITIAL_FORM);
    setSessions(isEdit ? baselineRef.current.sessions : []);
    setAttemptedSubmit(false);
  };

  const doSubmit = async () => {
    try {
      await submitEdition(
        {
          lo_id: loId,
          edition_code: form.edition_code || null,
          edition_name: form.edition_name || null,
          extra_info: form.extra_info || null,
          max_capacity: form.max_capacity,
          publish_date: form.publish_date,
        },
        sessions,
      );
      setSuccessOpen(true);
    } catch (e: any) {
      setSubmitError(
        isSessionOverlapError(e)
          ? formatSessionOccupiedError(e, t)
          : (getEditionCapacityErrorLabel(e, t) ??
              (getAllErrorsAsString(e) || String(t("feedback.error")))),
      );
    }
  };

  const handleConfirm = () => {
    if (readOnly) return;
    setAttemptedSubmit(true);
    if (
      publishDateMissing ||
      nameTooLong ||
      codeTooLong ||
      extraTooLong ||
      capacityMissing ||
      capacityInvalid
    )
      return;
    if (sessionsBeforePublishDate) {
      openToast({
        severity: "error",
        message: String(t("create.errors.sessions-before-publish-date")),
      });
      return;
    }
    if (
      !isEdit &&
      activityDuration &&
      sessionsTotalMinutes(sessions) !== activityDuration
    ) {
      setDurationWarnOpen(true);
      return;
    }
    void doSubmit();
  };

  const handleDurationConfirm = () => {
    setDurationWarnOpen(false);
    void doSubmit();
  };

  const handleSuccessDismiss = () => {
    setSuccessOpen(false);
    navigate({ pathname: "..", search: `?kind=${kind}` }, { relative: "path" });
  };

  const sectionTitle = (title: string) => (
    <Typography
      sx={{
        fontSize: "20px",
        fontWeight: 700,
        color: theme.palette.text.primary,
      }}
    >
      {title}
    </Typography>
  );

  const mainInfoFields = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <InputCst
          id="edition-name"
          label={String(t("create.fields.name"))}
          value={form.edition_name}
          onChange={(e: any) => update({ edition_name: e.target.value })}
          maxValue={EDITION_NAME_MAX_LENGTH}
          error={nameTooLong}
          errorMessage={
            nameTooLong ? String(t("create.errors.name-too-long")) : ""
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <InputCst
          id="edition-code"
          label={String(t("create.fields.code"))}
          value={form.edition_code}
          onChange={(e: any) => update({ edition_code: e.target.value })}
          maxValue={EDITION_CODE_MAX_LENGTH}
          error={codeTooLong}
          errorMessage={
            codeTooLong ? String(t("create.errors.code-too-long")) : ""
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <DataPickerCst
          id="edition-publish-date"
          label={String(t("create.fields.publish-date"))}
          value={form.publish_date}
          onChange={(v: any) => {
            if (v && !dayjs(v).isValid()) return;
            update({
              publish_date: v
                ? typeof v === "string"
                  ? v
                  : v?.toISOString?.()
                : null,
            });
          }}
          required
          disabled={publishDateLocked}
          error={attemptedSubmit && publishDateMissing}
          errorMessage={
            attemptedSubmit && publishDateMissing
              ? String(t("create.errors.publish-date-required"))
              : ""
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <InputCst
          id="edition-max-capacity"
          type="number"
          min={1}
          required
          label={String(t("create.fields.max-capacity"))}
          value={form.max_capacity}
          onChange={(e: any) => update({ max_capacity: e.target.value })}
          error={(attemptedSubmit && capacityMissing) || capacityInvalid}
          errorMessage={
            capacityInvalid
              ? String(t("create.errors.max-capacity-min"))
              : attemptedSubmit && capacityMissing
                ? String(t("create.errors.max-capacity-required"))
                : ""
          }
        />
      </Grid>
      <Grid item xs={12}>
        <InputCst
          id="edition-extra-info"
          label={String(t("create.fields.extra-info"))}
          value={form.extra_info}
          onChange={(e: any) => update({ extra_info: e.target.value })}
          maxValue={EDITION_EXTRA_INFO_MAX_LENGTH}
          multiline
          rows={3}
          error={extraTooLong}
          errorMessage={
            extraTooLong ? String(t("create.errors.extra-info-too-long")) : ""
          }
        />
      </Grid>
    </Grid>
  );

  if (!isEdit && !canCreateEdition) return <Navigate to="/403" replace />;
  if (isEdit && !canViewEdition) return <Navigate to="/403" replace />;

  if (isEdit && !hydrated) {
    return (
      <AnimationFadeIn>
        <Box sx={{ py: 3, pr: 3 }}>
          <Breadcrumb sticky={false} />
          {detailError ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 8,
                color: theme.palette.text.primary,
                fontSize: "14px",
              }}
            >
              {t("feedback.load-error")}
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </AnimationFadeIn>
    );
  }

  return (
    <AnimationFadeIn>
      <Box sx={{ pb: 3, pr: 3 }}>
        <PageHeaderV2Cst>
          <Breadcrumb sticky={false} />

          <Typography
            sx={{
              fontSize: "28px",
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 3,
            }}
          >
            {readOnly
              ? t("edit.title-detail")
              : isEdit
                ? t("edit.title")
                : t("create.title")}
          </Typography>
        </PageHeaderV2Cst>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
          }}
        >
          {sectionTitle(t("create.main-info"))}

          {readOnly ? (
            <WizardReadOnlyArea>{mainInfoFields}</WizardReadOnlyArea>
          ) : (
            mainInfoFields
          )}

          {!isEdit && (
            <>
              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {sectionTitle(t("create.sessions"))}
                {sessions.length > 0 && (
                  <ButtonTertiary
                    startIcon={<Plus size={16} />}
                    onClick={handleAddSession}
                    disabled={readOnly || publishDateMissing}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {t("create.add-session")}
                  </ButtonTertiary>
                )}
              </Box>

              {sessions.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 4,
                    color: theme.palette.text.primary,
                    background: theme.palette.backgroundPrimaryControl,
                    fontSize: "13px",
                    borderRadius: "12px",
                  }}
                >
                  <ButtonTertiary
                    startIcon={<Plus size={16} />}
                    onClick={handleAddSession}
                    disabled={readOnly || publishDateMissing}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {t("create.add-session")}
                  </ButtonTertiary>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {sessions.map((session, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        padding: "12px 16px",
                        borderRadius: "10px",
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {session.start_date
                            ? dayjs(session.start_date).format(
                                "DD/MM/YYYY HH:mm",
                              )
                            : "-"}
                          {" — "}
                          {session.end_date
                            ? dayjs(session.end_date).format("DD/MM/YYYY HH:mm")
                            : "-"}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: theme.palette.text.primary,
                          }}
                        >
                          {"tool" in session
                            ? session.tool
                              ? t(`session-wizard:method.${session.tool}.title`)
                              : "-"
                            : session.site?.label || "-"}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <ButtonTertiaryIcon
                          onClick={() => handleEditSession(index)}
                          disabled={readOnly}
                          aria-label="edit session"
                        >
                          <Pencil size={"1.125rem"} />
                        </ButtonTertiaryIcon>
                        <ButtonTertiaryIcon
                          onClick={() => handleRemoveSession(index)}
                          disabled={readOnly}
                          aria-label="remove session"
                        >
                          <Trash2 size={"1.125rem"} />
                        </ButtonTertiaryIcon>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 4,
          }}
        >
          <ButtonSecondary
            onClick={() => navigate(-1)}
            size={"md"}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
            }}
          >
            {t("back")}
          </ButtonSecondary>
          {!readOnly && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <ButtonTertiary
                onClick={handleReset}
                disabled={isSubmitting}
                size={"md"}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                }}
              >
                {t("create.reset")}
              </ButtonTertiary>
              <ButtonPrimary
                size={"md"}
                onClick={handleConfirm}
                disabled={isSubmitting}
              >
                {t("create.confirm")}
              </ButtonPrimary>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={durationWarnOpen}
        onClose={() => setDurationWarnOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.default,
            borderRadius: "16px",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle sx={{ padding: "20px 24px 8px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              {t("duration-warning.title")}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ padding: "12px 24px" }}>
          <Typography
            sx={{ fontSize: "14px", color: theme.palette.text.primary }}
          >
            {t("duration-warning.description")}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ padding: "12px 24px 20px", justifyContent: "flex-end", gap: 1 }}
        >
          <ButtonSecondary size="md" onClick={() => setDurationWarnOpen(false)}>
            {t("duration-warning.cancel")}
          </ButtonSecondary>
          <ButtonPrimary size="md" onClick={handleDurationConfirm}>
            {t("duration-warning.confirm")}
          </ButtonPrimary>
        </DialogActions>
      </Dialog>

      <WarningDialogCst
        open={!!submitError}
        title={String(t("feedback.error-title"))}
        description={submitError ?? ""}
        closeLabel={String(t("feedback.error-close"))}
        onClose={() => setSubmitError(null)}
      />

      <LOOutcomeModalCst
        open={successOpen}
        entityType="edition"
        mode={isEdit ? "edit" : "add"}
        onDismiss={handleSuccessDismiss}
      />
    </AnimationFadeIn>
  );
};

export default AddEditionPage;
